import { cn } from '@shared/components/shadcn';
import { Success, Time } from '@shared/icons';
import { calculateTimeUntil, NumberFormats as nf, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { BonusInfoSection } from '../../../BonusInfoSection/index';
import type { BundleStatus } from '../../../Offer/types';
import { ProgressBar } from '../../../ProgressBar/ProgressBar';
import { ProgressLabel } from '../../../ProgressBar/components/ProgressLabel';
import type { Step } from '../../types';

type StepIndicatorProps = {
  index: number;
  step: Step;
  isLastStep?: boolean;
  isOfferDisabled?: boolean;
  namespace: string;
  bundleStatus?: BundleStatus;
  offerType?: string;
};

function StepIndicator({ index, step, isLastStep, isOfferDisabled = false, namespace, bundleStatus, offerType }: StepIndicatorProps) {
  const t = useTranslations(namespace);
  const tOffer = useTranslations('offers.offer_validity');
  const currencyFormatter = useCurrencyFormatter();

  // Check if the expiration date is in the past (for styling purposes)
  const isExpiredByDate = step.expirationDate ? new Date(step.expirationDate) < new Date() : false;
  const isExpired = isExpiredByDate || step.status === 'EXPIRED';

  // Override step state to 'completed' if expired (for proper styling)
  const effectiveState: 'default' | 'active' | 'completed' = isExpired ? 'completed' : step.state;

  const shouldShowBonusInfo = (stepState: string) => {
    return stepState === 'active' || stepState === 'completed';
  };

  const isPlayAction = (actionType?: string) => {
    if (!actionType) return false;
    return actionType.startsWith('PLAY_');
  };

  const shouldShowRewardExpiry = () => {
    if (!step.expirationDate) return false;

    if (isPlayAction(step.actionType)) {
      return step.state === 'active' || step.state === 'completed' || isExpired;
    }

    return false;
  };

  const formatExpiryDate = (date: string, showCountdown?: boolean, stepStatus?: string) => {
    const parsedDate = new Date(date);
    const isExpired = parsedDate < new Date();

    // Show expired if date is in the past OR status is EXPIRED
    if (isExpired || stepStatus === 'EXPIRED') {
      return tOffer('badge', { date: parsedDate, state: 'expired' });
    }

    // Show countdown if within 24 hours (plain text, not animated)
    if (showCountdown) {
      const { hours, minutes } = calculateTimeUntil(parsedDate);
      return tOffer('badge', { date: parsedDate, state: 'ends_in', countdown: `${hours}h ${minutes}m` });
    }

    return tOffer('badge', { date: parsedDate, state: 'valid_until' });
  };

  const commonClasses = {
    indicator: 'flex shrink-0 items-center justify-center w-8 h-8 rounded-full paragraph-explanatory-strong text-foreground',
    label: 'text-foreground paragraph-strong',
  };

  const classes = {
    wrapper: `relative flex items-baseline`,
    indicator: {
      default: cn(commonClasses.indicator, 'bg-container'),
      active: cn(commonClasses.indicator, 'bg-container-active text-foreground-inverse'),
      completed: cn(commonClasses.indicator, 'bg-accent-green text-foreground dark:text-foreground-inverse'),
    },
    divider: `absolute left-4 top-8 w-px bg-container-secondary ${step.showProgress || step.bonusInfoData ? 'h-20' : 'h-14'}`,
    label: {
      default: commonClasses.label,
      active: commonClasses.label,
      completed: cn(commonClasses.label, 'text-foreground-subtle paragraph-strong'),
    },
    stepInfo: `flex flex-col w-full ml-4`,
  };

  const showCheckIcon = step.state === 'completed';
  const showExpiredIcon = (bundleStatus === 'EXPIRED' || step.status === 'EXPIRED') && step.state !== 'completed';
  const indicator = showCheckIcon ? <Success size="extra-small" /> : showExpiredIcon ? <Time size="extra-small" /> : <span>{index}</span>;

  return (
    <div className={classes.wrapper}>
      <div className={classes.indicator[effectiveState]}>{indicator}</div>
      {!isLastStep && <div className={classes.divider} />}
      <div className={classes.stepInfo}>
        <p className={classes.label[effectiveState]}>{step.label}</p>
        {step.description ? <p className="text-foreground-subtle paragraph-s mt-2">{step.description}</p> : null}

        {step.bonusInfoData && shouldShowBonusInfo(effectiveState) && (
          <div className={cn('mt-2', isOfferDisabled && 'opacity-50')} data-testid="bonus-balance">
            <div className="flex justify-between items-center">
              <p className="text-foreground paragraph-s-strong mt-1">{t('reward.bonus')}</p>
              <span className="text-foreground paragraph-s-strong mt-1">{currencyFormatter(step.bonusInfoData.currentAmount.fixed_amount).format(nf.WITH_DECIMALS).get()}</span>
            </div>
          </div>
        )}

        {step.showProgress && (
          <ProgressBar
            progress={step.progress}
            labelLeft={step.progressLabel}
            labelRight={<ProgressLabel progress={step.progress} format={step.progress.labelFormat || 'remainingCount'} formatAsCurrency={step.progress.isCurrency || false} />}
            showTooltip={step.showProgressTooltip}
          />
        )}

        {step.bonusInfoData && (
          <div className="mt-2">
            <BonusInfoSection bonusInfoData={step.bonusInfoData} bundleStatus={bundleStatus} isOfferDisabled={isOfferDisabled} namespace={namespace} offerType={offerType} />
          </div>
        )}

        {step.status !== 'COMPLETED' && shouldShowRewardExpiry() && step.expirationDate ? (
          <div className="mt-2">
            <p className={cn('text-foreground-subtle paragraph-s')} data-testid="reward-expiry-text">
              {formatExpiryDate(step.expirationDate, step.showCountdown, step.status)}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { StepIndicator };
